import { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

function FadeInSection({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) { setVisible(true); observer.unobserve(el); }
      },
      { threshold: 0.08, rootMargin: '0px 0px -30px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={`transition-all duration-800 ease-out ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} ${className}`}>
      {children}
    </div>
  );
}

const whyWorkWithMCT = [
  {
    icon: 'ri-heart-pulse-line',
    title: 'Purpose-Driven Work',
    description: 'Every day, you\u2019ll have the opportunity to help children, youth, and adults build confidence, develop life skills, and achieve meaningful goals.',
  },
  {
    icon: 'ri-team-line',
    title: 'Supportive Team Culture',
    description: 'You\u2019ll be part of a collaborative team that values respect, open communication, and continuous learning.',
  },
  {
    icon: 'ri-award-line',
    title: 'Professional Development',
    description: 'We are committed to helping our employees grow through ongoing training, mentorship, and career advancement opportunities.',
  },
  {
    icon: 'ri-money-dollar-circle-line',
    title: 'Competitive Compensation',
    description: 'We offer competitive wages and recognize the value our team members bring to the individuals we support.',
  },
  {
    icon: 'ri-star-smile-line',
    title: 'Work That Matters',
    description: 'The relationships you build today can have a lasting impact for years to come.',
  },
];

const qualitiesWeLookFor = [
  { icon: 'ri-heart-line', title: 'Compassion and empathy' },
  { icon: 'ri-shield-star-line', title: 'Professionalism and integrity' },
  { icon: 'ri-chat-3-line', title: 'Strong communication skills' },
  { icon: 'ri-check-double-line', title: 'Reliability and accountability' },
  { icon: 'ri-lightbulb-line', title: 'Positive problem-solving skills' },
  { icon: 'ri-shake-hands-line', title: 'Teamwork and collaboration' },
  { icon: 'ri-global-line', title: 'Respect for diversity and inclusion' },
  { icon: 'ri-book-open-line', title: 'A commitment to continuous learning' },
];

const currentOpportunities = [
  { icon: 'ri-user-heart-line', title: 'Child & Youth Workers', subtitle: 'CYW' },
  { icon: 'ri-mental-health-line', title: 'Developmental Service Workers', subtitle: 'DSW' },
  { icon: 'ri-community-line', title: 'Social Service Workers', subtitle: 'SSW' },
  { icon: 'ri-nurse-line', title: 'Personal Support Workers', subtitle: 'PSW' },
  { icon: 'ri-home-heart-line', title: 'Residential Counsellors', subtitle: '' },
  { icon: 'ri-calendar-check-line', title: 'Relief & Casual Staff', subtitle: '' },
  { icon: 'ri-user-star-line', title: 'Program Supervisors', subtitle: '' },
  { icon: 'ri-briefcase-line', title: 'Business Development Professionals', subtitle: '' },
  { icon: 'ri-file-list-3-line', title: 'Administrative Professionals', subtitle: '' },
];

const minimumQualifications = [
  'Relevant education or equivalent experience',
  'Excellent communication and interpersonal skills',
  'Ability to work independently and as part of a team',
  'Valid Ontario driver\u2019s licence (where applicable)',
  'Satisfactory Vulnerable Sector Check',
  'Valid First Aid & CPR Certification (or willingness to obtain)',
  'Commitment to person-centred care',
];

const hiringProcess = [
  { step: '1', title: 'Apply Online', description: 'Submit your resume through our careers page.' },
  { step: '2', title: 'Application Review', description: 'Our hiring team carefully reviews every application.' },
  { step: '3', title: 'Interview', description: 'Qualified candidates are invited to participate in an interview to discuss their experience and career goals.' },
  { step: '4', title: 'Reference & Background Checks', description: 'Successful candidates complete the required screening process.' },
  { step: '5', title: 'Welcome to MCT', description: 'Once hired, you\u2019ll receive orientation, training, and ongoing support to help you succeed.' },
];

export default function Careers() {
  const navigate = useNavigate();

  const scrollToOpportunities = () => {
    const el = document.getElementById('current-opportunities');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <main>
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-28 pb-14 md:pt-36 md:pb-20 overflow-hidden bg-background-50">
        <div className="absolute inset-0 top-0 h-full">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20professional%20team%20collaborating%20in%20a%20bright%20modern%20community%20care%20office%20environment%2C%20diverse%20group%20of%20healthcare%20and%20social%20service%20professionals%20smiling%20and%20engaged%2C%20natural%20sunlight%20through%20large%20windows%2C%20documentary%20editorial%20photography%20style%2C%20soft%20warm%20neutral%20tones%2C%20genuine%20candid%20workplace%20moment%2C%20comfortable%20and%20inviting%20atmosphere%2C%20authentic%20human%20connection%2C%20blurred%20background%20depth%2C%20hopeful%20uplifting%20mood&width=1600&height=800&seq=careers-hero-2026&orientation=landscape"
            alt="Join the MCT Residential Services team - Healthcare and social service professionals collaborating"
            title="Careers at MCT Residential Services"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/55 via-foreground-950/35 to-background-50"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 pt-8 md:pt-12">
          <div className="max-w-3xl">
            <span className="inline-block bg-white/15 backdrop-blur-sm text-white text-xs font-semibold px-4 py-1.5 rounded-full uppercase tracking-wider label-font mb-5 border border-white/20">
              Join Our Team
            </span>
            <h1 className="heading-font text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.08] mb-5 tracking-tight">
              Make a Difference <span className="italic text-accent-300">Every Day</span>
            </h1>
            <p className="text-white/75 text-base md:text-lg lg:text-xl leading-relaxed max-w-2xl font-light mb-8">
              At MCT Residential Services, a career is more than a job — it&apos;s an opportunity to make a meaningful impact in someone&apos;s life.
            </p>
            <p className="text-white/65 text-sm md:text-base leading-relaxed max-w-2xl font-light mb-8">
              We believe that exceptional care begins with exceptional people. That&apos;s why we invest in our team, foster a supportive workplace, and create opportunities for professional growth.
            </p>
            <p className="text-white/65 text-sm md:text-base leading-relaxed max-w-2xl font-light mb-10">
              If you&apos;re passionate about empowering others, building meaningful relationships, and helping individuals reach their full potential, we&apos;d love to hear from you.
            </p>
            <button
              onClick={scrollToOpportunities}
              className="inline-flex items-center justify-center gap-2 bg-white text-foreground-900 px-8 py-4 rounded-full text-base font-semibold label-font hover:bg-white/90 transition-all duration-300 cursor-pointer whitespace-nowrap group shadow-lg shadow-foreground-950/15"
            >
              View Current Opportunities
              <span className="w-5 h-5 flex items-center justify-center group-hover:translate-y-1 transition-transform">
                <i className="ri-arrow-down-line"></i>
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* Why Work With MCT */}
      <section className="py-10 md:py-14 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-12">
            <span className="inline-block bg-primary-100/70 text-primary-700 text-xs font-semibold px-4 py-1.5 rounded-full uppercase tracking-wider label-font mb-5">
              More Than a Job
            </span>
            <h2 className="heading-font text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 max-w-3xl mx-auto leading-tight">
              Why Work With MCT?
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {whyWorkWithMCT.map((item) => (
              <FadeInSection key={item.title}>
                <div className="group bg-background-50 rounded-2xl p-6 border border-background-200/70 hover:border-primary-200 transition-all duration-300 h-full flex flex-col items-center text-center">
                  <span className="w-14 h-14 flex items-center justify-center bg-primary-100 text-primary-600 rounded-2xl mb-4 group-hover:bg-primary-200 transition-colors">
                    <i className={`${item.icon} text-xl`}></i>
                  </span>
                  <h3 className="heading-font text-lg font-semibold text-foreground-900 mb-3 leading-snug">{item.title}</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">{item.description}</p>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* Who We're Looking For */}
      <section className="py-10 md:py-14 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14 items-start">
            <div className="lg:col-span-2">
              <span className="w-14 h-14 flex items-center justify-center bg-accent-100 text-accent-700 rounded-2xl mb-6">
                <i className="ri-search-eye-line text-xl"></i>
              </span>
              <span className="inline-block bg-accent-100/70 text-accent-800 text-xs font-semibold px-3.5 py-1 rounded-full uppercase tracking-wider label-font mb-4">
                Our Ideal Candidates
              </span>
              <h2 className="heading-font text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-5">
                Who We&apos;re Looking For
              </h2>
              <p className="text-foreground-600 text-base leading-relaxed">
                We&apos;re seeking compassionate, dependable, and motivated individuals who share our commitment to providing high-quality, person-centred support.
              </p>
            </div>
            <div className="lg:col-span-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {qualitiesWeLookFor.map((item) => (
                  <div key={item.title} className="flex items-center gap-3 bg-background-100 rounded-xl p-4 border border-background-200/70 hover:border-accent-200 transition-all duration-300 cursor-default">
                    <span className="w-10 h-10 flex items-center justify-center bg-accent-100 text-accent-700 rounded-lg flex-shrink-0">
                      <i className={`${item.icon} text-base`}></i>
                    </span>
                    <span className="font-medium text-foreground-800 text-sm label-font">{item.title}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Current Opportunities */}
      <section id="current-opportunities" className="py-10 md:py-14 bg-background-100 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-12">
            <span className="inline-block bg-secondary-100 text-secondary-800 text-xs font-semibold px-4 py-1.5 rounded-full uppercase tracking-wider label-font mb-5">
              Join Our Team
            </span>
            <h2 className="heading-font text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 max-w-3xl mx-auto leading-tight">
              Current Opportunities
            </h2>
            <p className="text-foreground-600 mt-5 max-w-2xl mx-auto leading-relaxed text-base md:text-lg">
              We are always interested in connecting with qualified professionals.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {currentOpportunities.map((item) => (
              <FadeInSection key={item.title}>
                <div className="group bg-background-50 rounded-xl p-6 border border-background-200/70 hover:border-secondary-300 transition-all duration-300 h-full flex flex-col items-center text-center">
                  <span className="w-12 h-12 flex items-center justify-center bg-secondary-100 text-secondary-700 rounded-xl mb-3 group-hover:bg-secondary-200 transition-colors">
                    <i className={`${item.icon} text-lg`}></i>
                  </span>
                  <h3 className="font-semibold text-foreground-900 text-sm label-font leading-snug mb-1">{item.title}</h3>
                  {item.subtitle && (
                    <span className="text-foreground-500 text-xs font-medium uppercase tracking-wider">{item.subtitle}</span>
                  )}
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* Minimum Qualifications */}
      <section className="py-10 md:py-14 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14 items-start">
            <div className="lg:col-span-2">
              <span className="w-14 h-14 flex items-center justify-center bg-secondary-100 text-secondary-700 rounded-2xl mb-6">
                <i className="ri-clipboard-line text-xl"></i>
              </span>
              <span className="inline-block bg-secondary-100/70 text-secondary-800 text-xs font-semibold px-3.5 py-1 rounded-full uppercase tracking-wider label-font mb-4">
                Requirements
              </span>
              <h2 className="heading-font text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-5">
                Minimum Qualifications
              </h2>
              <p className="text-foreground-600 text-base leading-relaxed">
                Qualifications vary by position but may include:
              </p>
            </div>
            <div className="lg:col-span-3">
              <div className="space-y-3">
                {minimumQualifications.map((qual, idx) => (
                  <div key={idx} className="flex items-start gap-3 bg-background-100 rounded-xl p-4 border border-background-200/70 hover:border-secondary-300 transition-all duration-300 cursor-default">
                    <span className="w-6 h-6 flex items-center justify-center bg-secondary-100 text-secondary-700 rounded-full flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-sm"></i>
                    </span>
                    <span className="font-medium text-foreground-800 text-sm label-font leading-relaxed">{qual}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Hiring Process */}
      <section className="py-10 md:py-14 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-12">
            <span className="inline-block bg-accent-100 text-accent-800 text-xs font-semibold px-4 py-1.5 rounded-full uppercase tracking-wider label-font mb-5">
              How to Apply
            </span>
            <h2 className="heading-font text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 max-w-3xl mx-auto leading-tight">
              Our Hiring Process
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {hiringProcess.map((item, idx) => (
              <FadeInSection key={item.step}>
                <div className="group bg-background-50 rounded-2xl p-6 border border-background-200/70 hover:border-accent-200 transition-all duration-300 h-full flex flex-col items-center text-center relative">
                  <span className="w-12 h-12 flex items-center justify-center bg-accent-500 text-white rounded-full mb-4 heading-font text-lg font-bold">
                    {item.step}
                  </span>
                  <h3 className="heading-font text-lg font-semibold text-foreground-900 mb-3 leading-snug">{item.title}</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">{item.description}</p>
                  {idx < hiringProcess.length - 1 && (
                    <div className="hidden lg:block absolute -right-3 top-1/2 -translate-y-1/2 z-10">
                      <span className="w-6 h-6 flex items-center justify-center text-accent-400">
                        <i className="ri-arrow-right-s-line text-xl"></i>
                      </span>
                    </div>
                  )}
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* Life at MCT — merged Culture + Commitment + Growth */}
      <section className="py-10 md:py-14 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-12">
            <span className="inline-block bg-primary-100/70 text-primary-700 text-xs font-semibold px-4 py-1.5 rounded-full uppercase tracking-wider label-font mb-5">
              Our Workplace
            </span>
            <h2 className="heading-font text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 max-w-3xl mx-auto leading-tight">
              Life at MCT
            </h2>
            <p className="text-foreground-600 mt-5 max-w-2xl mx-auto leading-relaxed text-base md:text-lg">
              We believe that great care starts with a team that feels valued, supported, and inspired to grow.
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14">
            <FadeInSection>
              <div className="bg-background-100 rounded-2xl p-8 md:p-10 border border-background-200/70 h-full">
                <span className="w-14 h-14 flex items-center justify-center bg-accent-100 text-accent-700 rounded-2xl mb-6">
                  <i className="ri-plant-line text-xl"></i>
                </span>
                <span className="inline-block bg-accent-100/70 text-accent-800 text-xs font-semibold px-3.5 py-1 rounded-full uppercase tracking-wider label-font mb-4">
                  Career Development
                </span>
                <h3 className="heading-font text-2xl md:text-3xl font-bold text-foreground-900 leading-tight mb-4">
                  Grow With Us
                </h3>
                <p className="text-foreground-600 text-base leading-relaxed mb-5">
                  Whether you&apos;re just starting out or bringing years of experience, we provide pathways to expand your skills, take on new responsibilities, and advance within the organization through structured mentorship, continuing education, and leadership development.
                </p>
                <div className="flex flex-wrap gap-2">
                  {['Mentorship Programs', 'Skills Training', 'Leadership Pathways', 'Education Support'].map((tag) => (
                    <span key={tag} className="inline-block bg-accent-50 text-accent-800 text-xs font-medium px-3 py-1.5 rounded-full border border-accent-100">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </FadeInSection>

            <FadeInSection>
              <div className="bg-background-100 rounded-2xl p-8 md:p-10 border border-background-200/70 h-full">
                <span className="w-14 h-14 flex items-center justify-center bg-secondary-100 text-secondary-700 rounded-2xl mb-6">
                  <i className="ri-shield-user-line text-xl"></i>
                </span>
                <span className="inline-block bg-secondary-100/70 text-secondary-800 text-xs font-semibold px-3.5 py-1 rounded-full uppercase tracking-wider label-font mb-4">
                  Our Culture
                </span>
                <h3 className="heading-font text-2xl md:text-3xl font-bold text-foreground-900 leading-tight mb-4">
                  A Culture of Respect
                </h3>
                <p className="text-foreground-600 text-base leading-relaxed mb-5">
                  We celebrate diversity, encourage open communication, and extend the same compassion to our team that we bring to the individuals we support. When our employees thrive, the people in our care thrive too.
                </p>
                <div className="flex flex-wrap gap-2">
                  {['Inclusive Environment', 'Open Communication', 'Team Collaboration', 'Employee Recognition'].map((tag) => (
                    <span key={tag} className="inline-block bg-secondary-50 text-secondary-800 text-xs font-medium px-3 py-1.5 rounded-full border border-secondary-100">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </FadeInSection>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="bg-primary-500 rounded-3xl p-10 md:p-16 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary-400/30 to-transparent"></div>
            <div className="relative z-10 max-w-2xl mx-auto">
              <span className="w-16 h-16 flex items-center justify-center bg-white/20 text-white rounded-2xl mx-auto mb-6">
                <i className="ri-briefcase-line text-2xl"></i>
              </span>
              <h2 className="heading-font text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-5">
                Ready to Make a Difference?
              </h2>
              <p className="text-white/80 text-base md:text-lg leading-relaxed mb-8">
                Join a team that&apos;s committed to changing lives through compassion, collaboration, and excellence.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <a
                  href="mailto:careers@mctresidential.com"
                  className="inline-flex items-center justify-center gap-2 bg-white text-primary-700 px-8 py-4 rounded-full text-base font-semibold label-font hover:bg-white/90 transition-all duration-300 cursor-pointer whitespace-nowrap group shadow-lg shadow-foreground-950/15"
                >
                  Apply Today
                  <span className="w-5 h-5 flex items-center justify-center group-hover:translate-x-1 transition-transform">
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </a>
                <span className="text-white/60 text-sm label-font">or</span>
                <a
                  href="mailto:careers@mctresidential.com"
                  className="inline-flex items-center justify-center gap-2 border border-white/30 text-white px-8 py-4 rounded-full text-base font-medium label-font hover:bg-white/10 hover:border-white/50 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-mail-send-line"></i>
                  </span>
                  careers@mctresidential.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}